import {
  EXPLORE_NON_PLACE_TYPES,
  EXPLORE_PAGE_CONTEXT,
  EXPLORE_PLACE_TYPES,
  EXPLORE_TYPE_ALL,
  EXPLORE_TYPE_GUIDE,
  EXPLORE_TYPES,
} from "@/constants/explore"
import { PlaceRef } from "@/types/entity"
import {
  ExploreConfigurationResponse,
  ExploreFilters,
  ExploreLocation,
  ExplorePlaceType,
  ExploreSearchContextType,
  ExploreType,
  SocialFilters,
} from "@/types/explore"
import type { LocationSeedSource } from "@/types/home-location-seed"
import { InspirationSummary } from "@/types/inspiration"
import { PlacePlaceholder, PlaceSummary, PlaceType } from "@/types/place"
import { PublicUser } from "@/types/user"
import { BoundingBox } from "@/types/util"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { formatPlaceType } from "../format-utils"
import { isClientSide } from "../is-client-side"
import { PagingMeta } from "../paging"
import { webStorage } from "../web-storage"
import { selectIsGeoAcquiring } from "./geolocation-selectors"
import { RootState } from "./redux-store"

export interface ExploreSliceState {
  loadingResults: boolean
  loadingGuides: boolean
  selectedType: ExploreType
  location: ExploreLocation | undefined
  queryId: string | undefined
  places: PlaceSummary[]
  guides: InspirationSummary[]
  locationGuides: InspirationSummary[]
  locationGuideOwners: PublicUser[]
  users: PublicUser[]
  drawerPosition: "open" | "closed"
  locationPickerInView: boolean
  searchString: string
  expandedMap: boolean
  nearbyPlaces: PlacePlaceholder[]
  recentLocationRefs: PlaceRef[]
  currentLocation: ExploreLocation | undefined
  isExploringMapArea: boolean
  isStreaming: boolean
  userRateLimited: boolean | undefined
  isLocationUserSelected: boolean
  /**
   * They pressed "Use current location": what they want is what is around
   * THEM, which is a different ask from the town that fix resolves to. Cleared
   * the moment they pick a place by hand.
   */
  isLocationNearMe: boolean
  /**
   * Home looked for a location to seed (device fix, then home, then IP) and
   * found none. The page asks them to pick a place.
   */
  isLocationUnknown: boolean
  /** Which step of the Home seed chain matched; ip drives the approximate banner. */
  locationSeedSource: LocationSeedSource | null
  /** Raw IP city when the seed was broadened to a region. */
  locationApproximateFrom: string | null
  /** App home "For you in …" location; independent of Explore page location. */
  homeLocation: ExploreLocation | undefined
  isHomeLocationUserSelected: boolean
  isHomeLocationNearMe: boolean
  isLocationSearchSuggested: boolean
  isAutoDetectingLocation: boolean
  // Auto-managed by `initLocationFromCurrentPosition` for the lat/lng → name window.
  isResolvingPositionLocation: boolean
  bbox: BoundingBox | undefined
  lastStrippedQuery: string | undefined
  filters: { [key in ExploreType]?: ExploreFilters }
  pagination: PagingMeta | undefined
  suggestedTabOrder: ExploreType[] | undefined
  socialFilters: SocialFilters
  showSmallUIMap: boolean
  configurations: { [key in ExplorePlaceType]?: ExploreConfigurationResponse }

  // For unified search
  unifiedSearchContext: ExploreSearchContextType
  isMultiSelectMode: boolean
  isSearchDialogOpen: boolean

  // For showing the typeahead result while searching related places
  pinnedTypeaheadPlace: PlaceSummary | undefined
}

export const RECENT_LOCATIONS_STORAGE_KEY = "exploreRecentLocationRefs"
const OLD_RECENT_LOCATIONS_STORAGE_KEY = "exploreRecentLocations"

const getInitialRecentLocationRefs = () => {
  if (!isClientSide()) return []
  try {
    const refs = webStorage.tryGetJSON<PlaceRef[]>(
      RECENT_LOCATIONS_STORAGE_KEY,
      { defaultValue: [] }
    )

    if (refs.length > 0) {
      return refs
    }

    try {
      const oldLocations = webStorage.tryGetJSON<
        Array<{ entity_ref: PlaceRef }>
      >(OLD_RECENT_LOCATIONS_STORAGE_KEY, { defaultValue: [] })

      if (oldLocations.length > 0) {
        const migratedRefs = oldLocations
          .map((loc) => loc?.entity_ref)
          .filter((ref): ref is PlaceRef => Boolean(ref))
          .slice(0, 5)

        if (migratedRefs.length > 0) {
          webStorage.setJSON(RECENT_LOCATIONS_STORAGE_KEY, migratedRefs)
          webStorage.removeItem(OLD_RECENT_LOCATIONS_STORAGE_KEY)
        }

        return migratedRefs
      }
    } catch (migrationError) {
      console.error("Failed to migrate old recent locations:", migrationError)
    }

    return []
  } catch (e) {
    console.error("Failed to parse recent location refs from storage:", e)
    return []
  }
}

export const exploreSliceInitialState: ExploreSliceState = {
  loadingResults: true,
  loadingGuides: true,
  selectedType: EXPLORE_TYPE_ALL,
  queryId: undefined,
  location: undefined,
  places: [],
  guides: [],
  locationGuides: [],
  locationGuideOwners: [],
  users: [],
  drawerPosition: "open",
  locationPickerInView: true,
  searchString: "",
  expandedMap: false,
  nearbyPlaces: [],
  recentLocationRefs: getInitialRecentLocationRefs(),
  currentLocation: undefined,
  isExploringMapArea: false,
  isStreaming: false,
  isLocationUserSelected: false,
  isLocationNearMe: false,
  isLocationUnknown: false,
  locationSeedSource: null,
  locationApproximateFrom: null,
  homeLocation: undefined,
  isHomeLocationUserSelected: false,
  isHomeLocationNearMe: false,
  isLocationSearchSuggested: false,
  isAutoDetectingLocation: false,
  isResolvingPositionLocation: false,
  filters: {},
  socialFilters: { type: null },
  showSmallUIMap: false,
  userRateLimited: undefined,
  bbox: undefined,
  lastStrippedQuery: undefined,
  pagination: undefined,
  suggestedTabOrder: undefined,
  configurations: {},
  unifiedSearchContext: EXPLORE_PAGE_CONTEXT,
  isMultiSelectMode: false,
  isSearchDialogOpen: false,
  pinnedTypeaheadPlace: undefined,
}

const exploreSlice = createSlice({
  name: "exploreSlice",
  initialState: exploreSliceInitialState,
  reducers: {
    reset: () => exploreSliceInitialState,
    setLoadingResults: (state, action: PayloadAction<boolean>) => {
      delete state.userRateLimited
      state.loadingResults = action.payload
    },
    setAutoDetectingLocation: (state, action: PayloadAction<boolean>) => {
      state.isAutoDetectingLocation = action.payload
    },
    setIsResolvingPositionLocation: (state, action: PayloadAction<boolean>) => {
      state.isResolvingPositionLocation = action.payload
    },
    setLoadingGuides: (state, action: PayloadAction<boolean>) => {
      state.loadingGuides = action.payload
    },
    setSelectedType: (state, action: PayloadAction<ExploreType>) => {
      state.selectedType = action.payload
    },
    setQueryId: (state, action: PayloadAction<string>) => {
      state.queryId = action.payload
    },
    setLocation: (
      state,
      action: PayloadAction<{
        location: ExploreLocation | undefined
        isUserSelected?: boolean
        isSearchSuggested?: boolean
      }>
    ) => {
      state.location = action.payload.location
      state.isLocationUserSelected = action.payload.isUserSelected ?? true
      // The fix resolving to a town arrives as a not-user-selected location; only
      // a place they picked by hand ends near-me.
      if (state.isLocationUserSelected) state.isLocationNearMe = false
      state.isLocationSearchSuggested =
        action.payload.isSearchSuggested ?? false
    },
    setLocationNearMe: (state, action: PayloadAction<boolean>) => {
      state.isLocationNearMe = action.payload
    },
    setHomeLocation: (
      state,
      action: PayloadAction<{
        location: ExploreLocation | undefined
        isUserSelected?: boolean
      }>
    ) => {
      state.homeLocation = action.payload.location
      state.isHomeLocationUserSelected = action.payload.isUserSelected ?? true
      if (state.isHomeLocationUserSelected) state.isHomeLocationNearMe = false
      if (action.payload.location) state.isLocationUnknown = false
      if (action.payload.isUserSelected) {
        state.locationSeedSource = null
        state.locationApproximateFrom = null
      }
    },
    setHomeLocationNearMe: (state, action: PayloadAction<boolean>) => {
      state.isHomeLocationNearMe = action.payload
    },
    setLocationUnknown: (state, action: PayloadAction<boolean>) => {
      state.isLocationUnknown = action.payload
    },
    setLocationSeedSource: (
      state,
      action: PayloadAction<LocationSeedSource | null>
    ) => {
      state.locationSeedSource = action.payload
      if (!action.payload) state.locationApproximateFrom = null
    },
    setLocationApproximateFrom: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.locationApproximateFrom = action.payload
    },
    setPlaces: (state, action: PayloadAction<PlaceSummary[]>) => {
      state.places = action.payload
    },
    setGuides: (state, action: PayloadAction<InspirationSummary[]>) => {
      state.guides = action.payload
    },
    setLocationGuides: (state, action: PayloadAction<InspirationSummary[]>) => {
      state.locationGuides = action.payload
    },
    setDrawerPosition: (state, action: PayloadAction<"open" | "closed">) => {
      state.drawerPosition = action.payload
    },
    setLocationPickerInView: (state, action: PayloadAction<boolean>) => {
      state.locationPickerInView = action.payload
    },
    setSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload
    },
    setExpandedMap: (state, action: PayloadAction<boolean>) => {
      state.expandedMap = action.payload
    },
    setNearbyPlaces: (state, action: PayloadAction<PlacePlaceholder[]>) => {
      state.nearbyPlaces = action.payload
    },
    addRecentLocation: (state, action: PayloadAction<ExploreLocation>) => {
      const entityRef = action.payload.entity_ref
      state.recentLocationRefs = [
        entityRef,
        ...state.recentLocationRefs.filter((ref) => ref !== entityRef),
      ].slice(0, 5)

      if (isClientSide()) {
        try {
          webStorage.setJSON(
            RECENT_LOCATIONS_STORAGE_KEY,
            state.recentLocationRefs
          )
        } catch (e) {
          console.error("Failed to save recent location refs to storage:", e)
        }
      }
    },
    addStreamedPlaces: (state, action: PayloadAction<PlaceSummary[]>) => {
      state.places.push(...action.payload)
    },
    stopPlaceStreaming: (state) => {
      state.loadingResults = false
    },
    setIsStreaming: (state, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload
    },
    setUserRateLimited: (state, action: PayloadAction<boolean>) => {
      state.userRateLimited = action.payload
    },
    setIsExploringMapArea: (state, action: PayloadAction<boolean>) => {
      state.isExploringMapArea = action.payload
    },
    setUnifiedSearchContext: (
      state,
      action: PayloadAction<ExploreSearchContextType>
    ) => {
      state.unifiedSearchContext = action.payload
    },
    setIsMultiSelectMode: (state, action: PayloadAction<boolean>) => {
      state.isMultiSelectMode = action.payload
    },
    setIsSearchDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSearchDialogOpen = action.payload
    },
    setBbox: (state, action: PayloadAction<BoundingBox | undefined>) => {
      state.bbox = action.payload
    },
    setLastStrippedQuery: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.lastStrippedQuery = action.payload
    },
    setFilters: (
      state,
      action: PayloadAction<{ type: ExploreType; filters: ExploreFilters }>
    ) => {
      state.filters[action.payload.type] = action.payload.filters
    },
    setPagination: (state, action: PayloadAction<PagingMeta | undefined>) => {
      state.pagination = action.payload
    },
    setSuggestedTabOrder: (
      state,
      action: PayloadAction<ExploreType[] | undefined>
    ) => {
      state.suggestedTabOrder = action.payload
    },
    setSocialFilters: (state, action: PayloadAction<SocialFilters>) => {
      state.socialFilters = action.payload
    },
    setShowSmallUIMap: (state, action: PayloadAction<boolean>) => {
      state.showSmallUIMap = action.payload
    },
    setConfiguration: (
      state,
      action: PayloadAction<ExploreConfigurationResponse>
    ) => {
      state.configurations[action.payload.place_type] = action.payload
    },
    setPinnedTypeaheadPlace: (
      state,
      action: PayloadAction<PlaceSummary | undefined>
    ) => {
      state.pinnedTypeaheadPlace = action.payload
    },
  },
})

const selectSliceState = (state: RootState) => state.exploreSlice

export const selectSelectedType = createSelector(
  [selectSliceState],
  (state) => state.selectedType
)

export const selectIsGuides = createSelector(
  [selectSelectedType],
  (selectedType) => selectedType === EXPLORE_TYPE_GUIDE
)

export const selectLocation = createSelector(
  [selectSliceState],
  (state) => state.location
)

export const selectExplorePlaces = createSelector(
  [selectSliceState],
  (state) => state.places
)

export const selectExplorePlacesExcludingLocation = createSelector(
  [selectExplorePlaces],
  (places) => places.filter((place) => place.type !== "location")
)

export const selectGuides = createSelector(
  [selectSliceState],
  (state) => state.guides
)

export const selectLocationGuides = createSelector(
  [selectSliceState],
  (state) => state.locationGuides
)

export const selectLocationGuidesCount = createSelector(
  [selectLocationGuides],
  (locationGuides) => locationGuides.length
)

export const selectExplorePlacesCount = createSelector(
  [selectExplorePlaces],
  (places) => places.length
)

export const selectLoadingResults = createSelector(
  [selectSliceState],
  (state) => state.loadingResults
)

export const selectLoadingGuides = createSelector(
  [selectSliceState],
  (state) => state.loadingGuides
)

export const selectSearchString = createSelector(
  [selectSliceState],
  (state) => state.searchString
)

export const selectFormattedType = createSelector(
  [selectSelectedType],
  (selectedType) => {
    switch (selectedType) {
      case EXPLORE_TYPE_ALL:
        return "places"
      case EXPLORE_TYPE_GUIDE:
        return "guides"
      default:
        return formatPlaceType(selectedType)
    }
  }
)

export const selectDrawerPosition = createSelector(
  [selectSliceState],
  (state) => state.drawerPosition
)

export const selectLocationPickerInView = createSelector(
  [selectSliceState],
  (state) => state.locationPickerInView
)

export const selectExpandedMap = createSelector(
  [selectSliceState],
  (state) => state.expandedMap
)

export const selectGroupedPlaces = createSelector(
  [selectExplorePlaces, selectSelectedType, selectSearchString],
  (places, selectedType, searchString) => {
    if (selectedType === EXPLORE_TYPE_ALL && !searchString) {
      return places.reduce(
        (acc, place) => {
          const type = place.type
          if (!acc[type]) acc[type] = []
          acc[type].push(place)
          return acc
        },
        {} as Record<PlaceType, PlaceSummary[]>
      ) as Record<ExploreType, PlaceSummary[]>
    }
    return { [selectedType]: places } as Record<ExploreType, PlaceSummary[]>
  }
)

export const selectNearbyPlaces = createSelector(
  [selectSliceState],
  (state) => state.nearbyPlaces
)

export const selectRecentLocationRefs = createSelector(
  [selectSliceState],
  (state) => state.recentLocationRefs
)

export const selectIsStreaming = createSelector(
  [selectSliceState],
  (state) => state.isStreaming
)

export const selectUserRateLimited = createSelector(
  [selectSliceState],
  (state) => state.userRateLimited
)

export const selectIsLocationUserSelected = createSelector(
  [selectSliceState],
  (state) => state.isLocationUserSelected
)

export const selectIsLocationNearMe = createSelector(
  [selectSliceState],
  (state) => state.isLocationNearMe
)

export const selectHomeLocation = createSelector(
  [selectSliceState],
  (state) => state.homeLocation
)

export const selectIsHomeLocationUserSelected = createSelector(
  [selectSliceState],
  (state) => state.isHomeLocationUserSelected
)

export const selectIsHomeLocationNearMe = createSelector(
  [selectSliceState],
  (state) => state.isHomeLocationNearMe
)

export const selectHomeLocationTitle = createSelector(
  [selectHomeLocation],
  (location) => location?.name ?? ""
)

export const selectIsLocationUnknown = createSelector(
  [selectSliceState],
  (state) => state.isLocationUnknown
)

export const selectLocationSeedSource = createSelector(
  [selectSliceState],
  (state) => state.locationSeedSource
)

export const selectLocationApproximateFrom = createSelector(
  [selectSliceState],
  (state) => state.locationApproximateFrom
)

export const selectIsLocationIpSeeded = createSelector(
  [selectLocationSeedSource],
  (source) => source === "ip"
)

export const selectShowApproximateLocationBanner = createSelector(
  [selectIsLocationIpSeeded, selectIsLocationUnknown],
  (ipSeeded, unknown) => !unknown && ipSeeded
)

export const selectIsLocationSearchSuggested = createSelector(
  [selectSliceState],
  (state) => state.isLocationSearchSuggested
)

export const selectIsExploringMapArea = createSelector(
  [selectSliceState],
  (state) => state.isExploringMapArea
)

export const selectUnifiedSearchContext = createSelector(
  [selectSliceState],
  (state) => state.unifiedSearchContext
)

export const selectIsMultiSelectMode = createSelector(
  [selectSliceState],
  (state) => state.isMultiSelectMode
)

export const selectIsSearchDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.isSearchDialogOpen
)

export const selectIsAutoDetectingLocation = createSelector(
  [selectSliceState],
  (state) => state.isAutoDetectingLocation || state.isResolvingPositionLocation
)

export const selectLocationTitle = createSelector(
  [
    selectLocation,
    selectIsGeoAcquiring,
    selectIsExploringMapArea,
    selectIsAutoDetectingLocation,
  ],
  (location, isAcquiring, isExploringMapArea, isAutoDetecting) => {
    if (isExploringMapArea) return "Map area"
    if (isAcquiring || isAutoDetecting) return "Locating…"
    return location?.name ?? ""
  }
)

export const selectBbox = createSelector(
  [selectSliceState],
  (state) => state.bbox
)

export const selectLastStrippedQuery = createSelector(
  [selectSliceState],
  (state) => state.lastStrippedQuery
)

export const selectQueryId = createSelector(
  [selectSliceState],
  (state) => state.queryId
)

export const selectExploreFilters = createSelector(
  [selectSliceState, selectSelectedType],
  (state, selectedType) => state.filters[selectedType]
)

export const selectPagination = createSelector(
  [selectSliceState],
  (state) => state.pagination
)

export const selectSuggestedTabOrder = createSelector(
  [selectSliceState],
  (state) => state.suggestedTabOrder
)

export const selectOrderedExploreTypes = createSelector(
  [selectSuggestedTabOrder, selectUnifiedSearchContext],
  (suggestedTabOrder, unifiedSearchContext) => {
    if (suggestedTabOrder && suggestedTabOrder.length > 0) {
      // Backend provides suggested order for place types
      // Build final list: "all" + suggested place types + non-place types
      const orderedTypes: ExploreType[] = [EXPLORE_TYPE_ALL]

      // Add place types from backend's suggested order
      suggestedTabOrder.forEach((type) => {
        if (type !== EXPLORE_TYPE_ALL && !orderedTypes.includes(type)) {
          orderedTypes.push(type)
        }
      })

      // Always append non-place types (like "guide"), unless we are in the unified search mode.
      // One day when we let you search by guide, we can enable the "guide" tab then
      if (unifiedSearchContext === EXPLORE_PAGE_CONTEXT) {
        EXPLORE_NON_PLACE_TYPES.forEach((type) => {
          if (!orderedTypes.includes(type)) {
            orderedTypes.push(type)
          }
        })
      }

      return orderedTypes
    }

    const types: ExploreType[] = [EXPLORE_TYPE_ALL]
    return unifiedSearchContext === EXPLORE_PAGE_CONTEXT
      ? EXPLORE_TYPES
      : [...types, ...EXPLORE_PLACE_TYPES]
  }
)

export const selectShowSmallUIMap = createSelector(
  [selectSliceState],
  (state) => state.showSmallUIMap
)

export const selectSocialFilters = createSelector(
  [selectSliceState],
  (state) => state.socialFilters
)

export const selectConfigurations = createSelector(
  [selectSliceState],
  (state) => state.configurations
)

export const selectEventConfiguration = createSelector(
  [selectConfigurations],
  (configurations) => configurations.event
)

export const selectPinnedTypeaheadPlace = createSelector(
  [selectSliceState],
  (state) => state.pinnedTypeaheadPlace
)

export const {
  setLoadingResults,
  setAutoDetectingLocation,
  setIsResolvingPositionLocation,
  setLoadingGuides,
  setSearchString,
  setSelectedType,
  setQueryId,
  setLocation,
  setLocationNearMe,
  setHomeLocation,
  setHomeLocationNearMe,
  setLocationUnknown,
  setLocationSeedSource,
  setLocationApproximateFrom,
  setPlaces,
  setGuides,
  setLocationGuides,
  setDrawerPosition,
  setLocationPickerInView,
  setExpandedMap,
  setNearbyPlaces,
  addRecentLocation,
  addStreamedPlaces,
  stopPlaceStreaming,
  setIsStreaming,
  setUserRateLimited,
  setIsExploringMapArea,
  setUnifiedSearchContext,
  setIsMultiSelectMode,
  setIsSearchDialogOpen,
  setBbox,
  setLastStrippedQuery,
  setFilters,
  setPagination,
  setSuggestedTabOrder,
  setSocialFilters,
  setShowSmallUIMap,
  setConfiguration,
  setPinnedTypeaheadPlace,
} = exploreSlice.actions

export default exploreSlice
